import useRedirect, { REDIRECT_QUERY } from "@/hooks/useRedirect";
import { useAuthStore } from "@/lib/auth/AuthStore";
import { markSignupPending } from "@/lib/ga4";
import { loginCompleted, mixpanelEvent, signupCompleted, updateUserInfoMixpanel } from "@/lib/mixpanels/mixpanel";
import { isExpertRedirect, navigateToExternalUrl } from "@/lib/utils/redirect";
import { resolveProfileStep } from "@/lib/auth/profileGuard";
import { HOME_PAGE, LOGIN_PAGE, SIGNUP_AFTER_USER_PROFILE } from "@/settings/constant";
import { useMutation } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { useCallback, useEffect, useRef } from "react";
import { getMe } from "src/api/getMe";
import LoginDialog from "src/store/loginDialog";
import { useSavedUserSourceStore } from "src/store/main";
import { fetchAccessToken } from "../../../api/auth";
import useSessionUrl from "../../../hooks/useSessionUrl";
import { getChannelDialogContent, getChannelPopupType, isSignUpChannel } from "../utils/getChannelDialogContent";
// 환경설정 분리
const KAKAO_AUTH_CONFIG = {
    REDIRECT_URI: "".concat(process.env.NEXT_PUBLIC_MARKET_API_ENDPOINT, "/auth/kakao/callback"),
    APP_ENDPOINT: process.env.NEXT_PUBLIC_APP_ENDPOINT
};
const useKakaoSignInUp = ()=>{
    const router = useRouter();
    const { query , pathname  } = router;
    const authStore = useAuthStore();
    const { getSessionUrl , setSessionUrl , removeSessionUrl  } = useSessionUrl();
    const { isOpenSignUpDialog , setState  } = LoginDialog((state)=>({
            isOpenSignUpDialog: state.isOpenSignUpDialog,
            setState: state.setState
        })) //회원가입, 로그인시 다이얼로그 store
    ;
    // Popup Viewed 메타를 다이얼로그 노출 useEffect 까지 전달하기 위한 ref
    const popupViewMetaRef = useRef(null);
    const getMeMutation = useMutation(getMe);
    const { redirectAgain , redirectPath  } = useRedirect();
    const fetchAccesTokenMutation = useMutation(fetchAccessToken);
    const { updateUserMeta  } = useSavedUserSourceStore((param)=>{
        let { updateUserMeta  } = param;
        return {
            updateUserMeta
        };
    });
    // 카카오 로그인 핸들러 개선
    const onKakao = useCallback(async ()=>{
        removeSessionUrl();
        redirectPath && setSessionUrl(redirectPath);
        try {
            const { Kakao  } = window;
            await Kakao.Auth.authorize({
                redirectUri: KAKAO_AUTH_CONFIG.REDIRECT_URI,
                state: "".concat(KAKAO_AUTH_CONFIG.APP_ENDPOINT).concat(router.pathname),
                // 마케팅 활용 및 서비스 관련 정보 수신 동의(서비스 약관 tag: marketing)를 동의 화면에 노출.
                // 동의 결과(promotion_agreed) 저장은 BE 콜백(/auth/kakao/callback)이 Kakao service_terms로 처리.
                serviceTerms: "marketing"
            });
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            handleError(message);
        }
    }, [
        pathname,
        redirectPath
    ]);
    // 인증 후 처리 로직 개선
    const processKakaoCallback = async (param)=>{
        let { identificationId , signupUser  } = param;
        try {
            const { accessToken  } = await fetchAccesTokenMutation.mutateAsync(identificationId);
            const { user  } = await getMeMutation.mutateAsync(accessToken);
            authStore.updateUser(user);
            updateUserMeta({
                mainSubject: user === null || user === void 0 ? void 0 : user.mainSubject,
                job: user === null || user === void 0 ? void 0 : user.job
            });
            trackUserLogin({
                signupUser,
                user
            });
            handleRedirect(user);
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            handleError(message);
        }
    };
    const trackUserLogin = (param)=>{
        let { signupUser , user  } = param;
        {
            (user === null || user === void 0 ? void 0 : user.job) && updateUserInfoMixpanel({
                userProfile: {
                    subject: user === null || user === void 0 ? void 0 : user.mainSubject,
                    userType: user === null || user === void 0 ? void 0 : user.job,
                    school: [],
                    birth: user === null || user === void 0 ? void 0 : user.birth
                },
                userId: user.id.toString()
            });
        }
        if (signupUser === "true") {
            signupCompleted({
                user_id: authStore.user.id.toString(),
                "Registration Method": "kakao",
                "Login Method": "kakao",
                $name: authStore.user.displayName,
                $email: authStore.user.email,
                "Phone Number": authStore.user.phoneNumber
            });
            // 가입 전환 이벤트는 프로필 입력이 끝난 뒤(useMemberProfileStep)에 발사한다.
            // 여기서는 "방금 가입했다 + 수단은 카카오" 표식만 남긴다
            markSignupPending("kakao", authStore.user.id.toString());
        }
        loginCompleted({
            user_id: authStore.user.id.toString(),
            "Registration Method": "kakao",
            "Login Method": "kakao",
            $name: authStore.user.displayName,
            $email: authStore.user.email,
            "Phone Number": authStore.user.phoneNumber
        });
    };
    const handleRedirect = (user)=>{
        //TODO 세션에 저장한거 보내주기
        const savedRedirectPath = getSessionUrl();
        // 프로필(job/subject/school·grade) 미완료 시 설정 화면으로. 판정은 전역 가드와 동일한 단일 규칙 사용.
        const isProfileIncomplete = resolveProfileStep(user) !== null;
        if (isProfileIncomplete && pathname.includes("login")) {
            router.push({
                pathname: "".concat(SIGNUP_AFTER_USER_PROFILE),
                query: savedRedirectPath ? {
                    [REDIRECT_QUERY]: savedRedirectPath,
                    alreadySignupUser: true
                } : {
                    alreadySignupUser: true
                }
            });
            return;
        } else if (isProfileIncomplete) {
            router.push({
                pathname: "".concat(SIGNUP_AFTER_USER_PROFILE),
                query: savedRedirectPath && {
                    [REDIRECT_QUERY]: savedRedirectPath
                }
            });
            return;
        }
        // 로그인 성공 후 리다이렉트 처리
        // expert.solvook.com에서 온 경우 redirectPath(예: expert.solvook.com/handouts)로 리다이렉트
        if (typeof savedRedirectPath === "string" && isExpertRedirect(savedRedirectPath)) {
            navigateToExternalUrl(savedRedirectPath);
            return;
        }
        if (savedRedirectPath) {
            router.push(savedRedirectPath);
            return;
        }
        router.push(HOME_PAGE);
    };
    const handleError = (status, channelInfo)=>{
        const closeAndRedirect = ()=>{
            redirectAgain(LOGIN_PAGE);
            setState("isOpenSignUpDialog", false);
        };
        if (status === "409" && (channelInfo === null || channelInfo === void 0 ? void 0 : channelInfo.channel)) {
            const { title , content , maskedPhone , maskedEmail  } = getChannelDialogContent(channelInfo);
            // MX-601/603 (로그인) | MX-607/609 (회원가입) — Popup Viewed
            // pathname 으로 로그인/회원가입 컨텍스트 분기
            const isSignupContext = pathname.includes("signup");
            const position = isSignupContext ? "회원가입 - 카카오로 가입하기" : "로그인 - 카카오로 로그인";
            const popupType = getChannelPopupType(channelInfo.channel);
            // 다이얼로그가 실제 노출되는 시점(아래 isOpenSignUpDialog useEffect)에 발사하도록 메타만 보관.
            popupViewMetaRef.current = {
                Position: position,
                "Popup Type": popupType,
                "Popup Title": title
            };
            setState("openSignUpDialogContents", {
                title,
                content,
                btnLabel: "로그인하러 가기",
                maskedPhone,
                maskedEmail,
                onOk: ()=>{
                    // MX-602 (로그인) | MX-608 (회원가입) — Popup Button Clicked
                    mixpanelEvent("Popup Button Clicked", {
                        Position: position,
                        "Popup Type": popupType,
                        "Popup Title": title,
                        "Button Name": "로그인하러 가기"
                    });
                    closeAndRedirect();
                }
            });
        } else {
            setState("openSignUpDialogContents", {
                title: "로그인에 실패했어요.",
                content: "다시 시도해주세요. 문제가 지속되면 고객센터로 문의해주세요.",
                btnLabel: "로그인하러 가기",
                onOk: closeAndRedirect
            });
        }
        setState("isOpenSignUpDialog", true);
    };
    useEffect(()=>{
        const { signupUser , identificationId , statusCode , channel , maskedPhone , maskedEmail  } = query;
        //TODO  identificationId 있을경우 로그인 처리
        if (statusCode && statusCode !== "200") {
            const channelStr = typeof channel === "string" && isSignUpChannel(channel) ? channel : undefined;
            const channelInfo = channelStr ? {
                channel: channelStr,
                maskedPhone: typeof maskedPhone === "string" ? maskedPhone : null,
                maskedEmail: typeof maskedEmail === "string" ? maskedEmail : null
            } : undefined;
            handleError(statusCode.toString(), channelInfo);
            return;
        }
        if (identificationId) {
            processKakaoCallback({
                signupUser: signupUser.toString(),
                identificationId: identificationId.toString()
            });
        }
    // query 변경 시에만 재실행 의도. handleError/processKakaoCallback은 동일 렌더 클로저에서 호출됨
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        query
    ]);
    // 다이얼로그가 실제로 노출되는 시점에 'Popup Viewed' 발사 (MX-601/603/607/609).
    // 카카오 OAuth 콜백 직후 fresh load 에서 useEffect 와 mixpanel snippet(afterInteractive) 로딩이
    // 경합해 이벤트가 유실되는 race 를 완화하기 위해 짧은 deferral 을 둠.
    // TODO: App Router 이관 시 mixpanelEvent 자체 큐잉 + _document beforeInteractive 로 대체.
    useEffect(()=>{
        if (!isOpenSignUpDialog || !popupViewMetaRef.current) return;
        const meta = popupViewMetaRef.current;
        popupViewMetaRef.current = null // 1회 발화 가드
        ;
        const timer = setTimeout(()=>{
            mixpanelEvent("Popup Viewed", meta);
        }, 250);
        return ()=>clearTimeout(timer);
    }, [
        isOpenSignUpDialog
    ]);
    return {
        onKakao
    };
};
export default useKakaoSignInUp;
